/**
 * Utility functions for date handling
 */

/**
 * Formats a date string in the format "ddd, dd-MMM-yyyy"
 * @param dateString ISO date string (YYYY-MM-DD)
 * @returns Formatted date string (e.g., "Mon, 11-Mar-2025")
 */
export const formatDateWithDayOfWeek = (dateString: string): string => {
  try {
    // Ensure we have a proper date string
    if (!dateString) return '';
    
    // Create a Date object from the date string
    const date = new Date(dateString);
    
    // Check for invalid date
    if (isNaN(date.getTime())) return '';
    
    // Get date parts directly
    const weekday = date.toLocaleString('en-US', { weekday: 'short' });
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    
    // Return formatted date
    return `${weekday}, ${day}-${month}-${year}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString; // Return original string on error
  }
};

/**
 * Formats a date as month and year only
 * @param dateString ISO date string (YYYY-MM-DD)
 * @returns Formatted date string (e.g., "March 2025")
 */
export const formatDateAsMonthYear = (dateString: string): string => {
  try {
    // Ensure we have a proper date string
    if (!dateString) return '';
    
    // Create a Date object from the date string
    const date = new Date(dateString);
    
    // Check for invalid date
    if (isNaN(date.getTime())) return '';
    
    // Format as month and year
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  } catch (error) {
    console.error('Error formatting date as month/year:', error);
    return dateString; // Return original string on error
  }
};

/**
 * Converts a date from ISO format to display format and back
 * @param dateString Date string to be parsed
 * @param toDisplay If true, converts ISO to display format; if false, converts display to ISO
 * @returns Converted date string
 */
export const convertDateFormat = (dateString: string, toDisplay: boolean): string => {
  if (toDisplay) {
    return formatDateWithDayOfWeek(dateString);
  } else {
    // Extract the parts from "ddd, dd-MMM-yyyy" format
    const parts = dateString.split(', ')[1].split('-');
    const day = parts[0];
    const month = new Date(`${parts[1]} 1, 2000`).getMonth() + 1; // Convert month name to number
    const year = parts[2];
    
    // Return as ISO format (YYYY-MM-DD)
    return `${year}-${month.toString().padStart(2, '0')}-${day}`;
  }
};

/**
 * Calculate the first and last day of a month from a Date object
 * @param date The date to calculate the range for
 * @returns An object with firstDay and lastDay properties as ISO date strings (YYYY-MM-DD)
 */
export const calculateMonthRange = (date: Date): { firstDay: string, lastDay: string } => {
  // Create a new date for the first day of the month
  const firstDate = new Date(date.getFullYear(), date.getMonth(), 1);
  
  // Create a new date for the last day of the month
  const lastDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  
  // Format dates as ISO strings (YYYY-MM-DD)
  const firstDay = `${firstDate.getFullYear()}-${String(firstDate.getMonth() + 1).padStart(2, '0')}-${String(firstDate.getDate()).padStart(2, '0')}`;
  const lastDay = `${lastDate.getFullYear()}-${String(lastDate.getMonth() + 1).padStart(2, '0')}-${String(lastDate.getDate()).padStart(2, '0')}`;
  
  return { firstDay, lastDay };
};

/**
 * Parses a time string in "HH:mm" format to minutes since midnight
 * @param timeString Time string in "HH:mm" format
 * @returns Minutes since midnight, or null if invalid format
 */
export const parseTimeString = (timeString: string): number | null => {
  try {
    const [hours, minutes] = timeString.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return null;
    return hours * 60 + minutes;
  } catch (e) {
    console.error('Error parsing time string:', timeString, e);
    return null;
  }
};

/**
 * Formats a Date as an ISO date string (YYYY-MM-DD) using local time, not UTC.
 * 
 * IMPORTANT: This function preserves the date as it appears in the local timezone.
 * Using toISOString().split('T')[0] can cause timezone-related bugs because
 * it converts to UTC first, which can shift dates across day boundaries.
 * 
 * @param date The Date object to format
 * @returns YYYY-MM-DD string representing the date in local timezone
 */
export const formatLocalDateAsISOString = (date: Date): string => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};
